import { CircularProgress, Grid } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { noop } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import envConfig from '../../../../config/environment-config';
import { RoutesEnum } from '../../../../constants';
import { useAlert } from '../../../../contexts/pharmaceutical-portal/Alert.context';
import { useLoader } from '../../../../contexts/pharmaceutical-portal/Loader.context';
import { pharmaceuticalDataService, signupPharmaceuticalService, verifyRecaptchaService } from '../../../../services';
import { PharmaceuticalInputBox, SelectInput } from '../../common/components';
import { IPharmaceuticalCrendentials, IUseFormData, LocalStorageKeysEnum, PageModeEnum } from '../../common/contracts';
import { localStorageMethods } from '../../common/helpers';
import { Button } from '../../common/styles';
import { Box, Container, ReCaptchaWrapper } from '../../common/styles/FormStyles';
import { genderList, handlePharmaceuticalDefaultValueData, pharmaceuticalFormAlertMessages, preparePharmaceuticalCreateData, preparePharmaceuticalUpdateData, setPharmaceuticalDefaultValues } from '../helpers';
import { inputMUIDefaultStyles, InputsBox } from '../styles';
import { CellphoneInputs } from './CellphoneInputs';
import { EmailBoxInputs } from './EmailBoxInputs';
import { PasswordBoxInputs, UpdatePasswordBoxInputs } from './PasswordBoxInputs';
import ReCAPTCHA from 'react-google-recaptcha';

export function PharmaceuticalForm({
  mode,
}: {
  mode: PageModeEnum
}): JSX.Element {
  const queryClient = useQueryClient();
  const { push } = useHistory();
  const { showLoader, hideLoader } = useLoader();
  const [isLoadingDefaultValue, setIsLoadingDefaultValue] = useState<boolean>(true);
  const { showAlert, hideAlert } = useAlert();
  const [callApi, setCallApi] = useState<boolean>(false);
  const pharmaceuticalCredentials = localStorageMethods.getItem<IPharmaceuticalCrendentials>(LocalStorageKeysEnum.PHARMACEUTICAL_CREDENTIALS);
  const defaultCpfValue = localStorageMethods.getItem<string>(LocalStorageKeysEnum.PHARMACEUTICAL_CRFUF_LOGIN);
  const pageIsBlocked = mode === PageModeEnum.CREATE && !defaultCpfValue;
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [isRecaptchaValid, setIsRecaptchaValid] = useState<boolean>(false);
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    control,
    setValue,
    getValues,
    watch,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const formData: IUseFormData = {
    register,
    handleSubmit,
    errors,
    setError,
    control,
    setValue,
    watch,
    getValues,
    unregister: noop,
  };

  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setIsRecaptchaValid(false);
    setIsRecaptchaLoading(false);
  };

  const handleRecaptchaChange = async (
    value: string | null,
  ): Promise<void> => {
    if (!value) {
      resetRecaptcha();
      return;
    }

    try {
      setIsRecaptchaLoading(true);
      setIsRecaptchaValid(false);

      const response =
        await verifyRecaptchaService.verifyRecaptcha(value);

      if (!response.data) {
        resetRecaptcha();

        showAlert(
          'ReCAPTCHA inválido',
          'Não foi possível validar o reCAPTCHA. Tente novamente.',
          'error',
        );

        setTimeout(() => hideAlert(), 3000);
        return;
      }

      setIsRecaptchaValid(true);
    } catch {
      resetRecaptcha();

      showAlert(
        'ReCAPTCHA inválido',
        'Não foi possível validar o reCAPTCHA. Tente novamente.',
        'error',
      );

      setTimeout(() => hideAlert(), 3000);
    } finally {
      setIsRecaptchaLoading(false);
    }
  };

  const createPharmaceutical = async (registrationFormData: any) => {
    const { success } = pharmaceuticalFormAlertMessages;
    const preparedData = preparePharmaceuticalCreateData(registrationFormData);
    const result = await signupPharmaceuticalService.register(preparedData);
    const data = result.data;

    localStorageMethods.setItem<IPharmaceuticalCrendentials>(LocalStorageKeysEnum.PHARMACEUTICAL_CREDENTIALS, {
      access_token: data?.access_token,
      refresh_token: data?.refresh_token,
      healthPassportId: data?.userinfo?.uuid,
      userId: data?.userinfo?.userId,
      email: data?.userinfo?.email,
      firstName: data?.userinfo?.firstName,
      lastName: data?.userinfo?.lastName,
      fullName: data?.userinfo?.fullName,
      crf: data?.userinfo?.crf,
      crfUf: data?.userinfo?.crfUf,
      avatar: data?.userinfo?.avatar,
      programId: envConfig.boehringerProgramCode,
    });

    showAlert(
      pharmaceuticalFormAlertMessages.success.title,
      pharmaceuticalFormAlertMessages.success.subTitle,
      pharmaceuticalFormAlertMessages.success.status,
    );
    showAlert(success?.title || '', success?.subTitle || '', 'success');
    setTimeout(() => hideAlert(), 2000);
    setTimeout(() => window.location.href = '/', 2200);
  }

  const updatePharmaceutical = async (registrationFormData: any) => {
    const body = preparePharmaceuticalUpdateData(
      registrationFormData,
      pharmaceuticalCredentials?.healthPassportId || '',
    );

    await pharmaceuticalDataService.updatePharmaceuticalData(body);
    await queryClient.invalidateQueries([LocalStorageKeysEnum.PHARMACEUTICAL_DATA, pharmaceuticalCredentials?.healthPassportId || '']);

    if (pharmaceuticalCredentials) {
      localStorageMethods.setItem<IPharmaceuticalCrendentials>(LocalStorageKeysEnum.PHARMACEUTICAL_CREDENTIALS, {
        ...pharmaceuticalCredentials,
        email: body.user.email,
        fullName: body.user.name,
      })
    }

    setTimeout(() => hideAlert(), 2000);
    setTimeout(() => push(`${RoutesEnum.PHARMACEUTICAL_PANEL_DASHBOARD}`), 2200);
    return showAlert(
      pharmaceuticalFormAlertMessages.successUpdatePharmaceutical.title,
      pharmaceuticalFormAlertMessages.successUpdatePharmaceutical.subTitle,
      pharmaceuticalFormAlertMessages.successUpdatePharmaceutical.status,
    );
  }

  const getCreateDefaultValue = () => ({
    crf: defaultCpfValue?.split('-')[0],
    crfUf: defaultCpfValue?.split('-')[1],
  });

  const getUpdateDefaultValue = () => {
    const { data: pharmaceuticalData, isLoading } = pharmaceuticalDataService.getPharmaceuticalDataWithCache(
      Number(envConfig.boehringerProgramCode),
      pharmaceuticalCredentials?.healthPassportId || '',
      setCallApi
    );

    useEffect(() => {
      setIsLoadingDefaultValue(isLoading);
    }, [isLoading]);

    return handlePharmaceuticalDefaultValueData(pharmaceuticalData?.data);
  };

  /* eslint-disable */
  const modeOptions = {
    [PageModeEnum.CREATE.toString()]: {
      buttonName: 'Continue',
      dataDestiny: createPharmaceutical,
      getDefaultValue: getCreateDefaultValue as any,
      passwordComponent: (formData: IUseFormData) => <PasswordBoxInputs formData={formData} />,
      cancelButton: () => <></>,
    },
    [PageModeEnum.UPDATE.toString()]: {
      buttonName: 'Salvar',
      dataDestiny: updatePharmaceutical,
      getDefaultValue: getUpdateDefaultValue as any,
      passwordComponent: (formData: IUseFormData) => <UpdatePasswordBoxInputs formData={formData} />,
      cancelButton: () => {
        return (
          <Button type='cancel' onClick={() => push(`${RoutesEnum.PHARMACEUTICAL_PANEL_DASHBOARD}`)}>
            <button>
              Cancelar alterações
            </button>
          </Button>
        )
      },
    },
  };
  /* eslint-disable */

  const defaultValue = modeOptions[mode]?.getDefaultValue();

  useEffect(() => {
    setPharmaceuticalDefaultValues(defaultValue, setValue);
  }, [callApi]);

  const onSubmit = async (
    registrationFormData: any,
  ): Promise<void> => {
    if (
      mode === PageModeEnum.CREATE
      && !isRecaptchaValid
    ) {
      showAlert(
        'ReCAPTCHA obrigatório',
        'Valide o reCAPTCHA antes de continuar.',
        'error',
      );

      setTimeout(() => hideAlert(), 3000);
      return;
    }

    try {
      showLoader();

      await modeOptions[mode]?.dataDestiny(
        registrationFormData,
      );
    } catch (e: any) {
      if (mode === PageModeEnum.CREATE) {
        resetRecaptcha();
      }

      const errorMessage =
        e?.response?.data?.message
        ?? pharmaceuticalFormAlertMessages?.subTitle;

      showAlert(
        pharmaceuticalFormAlertMessages
          ?.defaultError.title || '',
        errorMessage || '',
        'error',
      );

      console.error(e);
    } finally {
      hideLoader();
    }
  };

  if (isLoadingDefaultValue && mode === PageModeEnum.UPDATE) {
    return (
      <div style={{ margin: '200px auto', marginLeft: '48%' }}>
        <CircularProgress />
      </div>
    );
  }

  if (pageIsBlocked) {
    push(RoutesEnum.PHARMACEUTICAL_LOGIN_PAGE);
    return <></>;
  }

  return (
    <Container>
      {mode === PageModeEnum.CREATE && (
        <Alert
          severity="warning"
          icon={false}
          style={{ marginTop: 15, marginBottom: 30, alignItems: 'center', textAlign: 'center' }}
        >
          <AlertTitle style={{ width: '100%', fontWeight: 'bold' }}>
            Aviso
          </AlertTitle>
          Ao me cadastrar no Programa Abraçar a Vida declaro que sou responsável pela veracidade e exatidão dos dados informados durante o cadastro.
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <PharmaceuticalInputBox
            formData={formData}
            defaultValue={{
              cpf: defaultValue?.cpf,
              crf: defaultValue?.crf,
              crfUf: defaultValue?.crfUf,
            }}
          />
        </Box>
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <SelectInput
                title="Gênero"
                isRequired
                list={genderList}
                style={inputMUIDefaultStyles.normal}
                formData={formData}
                propertieFormName="gender"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CellphoneInputs formData={formData} defaultValue={defaultValue} />
            </Grid>
          </Grid>
        </Box>
        <EmailBoxInputs formData={formData} defaultValue={defaultValue} />
        <Box>
          {modeOptions[mode]?.passwordComponent(formData)}
        </Box>

        {mode === PageModeEnum.CREATE && (
          <Alert
            severity="info"
            icon={false}
            style={{ marginTop: -50, marginBottom: 30, alignItems: 'center', textAlign: 'center' }}
          >
            <AlertTitle style={{ width: '100%', fontWeight: 'bold' }}>
              Importante:
            </AlertTitle>
            Se você já usa o Portal Médico ou o Portal do Paciente, não precisa criar outra senha, a mesma senha funciona em todos os portais.
          </Alert>
        )}

        {mode === PageModeEnum.CREATE && (
          <ReCaptchaWrapper>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={envConfig.googleSiteKey}
              onChange={handleRecaptchaChange}
              onExpired={resetRecaptcha}
              onErrored={resetRecaptcha}
            />
          </ReCaptchaWrapper>
        )}

        <InputsBox>
          {modeOptions[mode]?.cancelButton()}
          <Button type='default'>
            <button
              type="submit"
              className="btn_new_farmaceutico"
              disabled={
                mode === PageModeEnum.CREATE
                && (
                  !isRecaptchaValid
                  || isRecaptchaLoading
                )
              }
            >
              {
                isRecaptchaLoading
                  ? 'Validando...'
                  : modeOptions[mode]?.buttonName || ''
              }
            </button>
          </Button>
        </InputsBox>
      </form>
    </Container>
  );
}